/**
 * Where the sun's shadows of buildings fall on one storey: a pure model (no pixi), drawn by
 * BuildingShadowLayer (building_shadow.ts).
 *
 * Everything solid above a storey's floor casts onto it: walls (as boxes standing on their own
 * storey), the floor slabs of the storeys above, and roofs (a pitched roof rising to its ridge). Each
 * caster's ground footprint is swept along the sun from its lowest to its highest point, so a shadow's
 * length is the caster's height above THIS storey -- a tower's walls cast three storeys' worth onto
 * the ground and one storey's worth onto its own top floor.
 *
 * A storey only receives where it has a surface: the ground everywhere, an upper storey where its
 * floor art is drawn (floor_art.ts: the art stops short of a hole's cells, so the hole looks wider than
 * they are). An opening in a floor is not a surface, and it is also a gap in that slab, so the storey
 * below gets the sunlight through it. Clipping to the drawn floor is what keeps an upper storey's
 * shade from hanging over a hole in mid-air; the storey below draws that shade where it really lands.
 *
 * Heights are measured in the same pixels the storeys are drawn apart in (STOREY_RISE_PX), so the sun
 * here is the people's sun: a pixel of height moves the shadow `sun.x` across and `sun.y` down.
 */
import { pitchedRoofPiece } from "game-common/room/autotile_roof"
import { drawnFloorRects, FloorRect } from "./floor_art"

const T = 32

/** One storey as a shadow sees it. Cells are "x,y" keys. */
export interface ShadowStorey {
    /** 0 is the ground. */
    floor: number
    /** The ground receives everywhere; an upper storey only on its `surface`. */
    ground: boolean
    /** Each wall's own cell (its parent). */
    walls: ReadonlySet<string>
    /** Where this storey can be stood on: its floor, without openings. */
    surface: ReadonlySet<string>
    /** This storey's floor as it shades the storeys below: no openings, no stairwells. */
    slab: ReadonlySet<string>
    /** Roof cells on this storey, and which of them are pitched. */
    roofs: ReadonlySet<string>
    pitched: ReadonlySet<string>
}

export interface BuildingShadowOptions {
    /** Ground displacement per pixel of height (the people's sun). */
    sun: { x: number; y: number }
    /** How far apart storeys stand (STOREY_RISE_PX). */
    rise: number
    /** How high a roof is drawn over its storey (ROOF_UPLIFT_PX). */
    roofLift: number
    /** Shade under a roof or the floor above. False: indoors stays lit, outside keeps its shadows. */
    indoors: boolean
}

/** A convex polygon as flat x, y pairs, in the receiving storey's own pixels. */
export type ShadowPolygon = number[]

/**
 * Where a wall stands inside its cell: the body of a north-south run spans these columns, an east-west
 * run is this deep and stands on the cell's bottom edge. Measured from wall-stone (timber-frame-wall
 * uses its geometry); the fence walls stand within a couple of pixels of it.
 */
export const WALL_BODY = { left: 10, right: 22, depth: 9 }
/** How tall a wall's own art is (its top band sits this far above its footprint). */
export const WALL_TOP = 40

interface Caster { x0: number; y0: number; x1: number; y1: number; low: number; high: number }

const parse = (key: string): [number, number] => {
    const comma = key.indexOf(",")
    return [Number(key.slice(0, comma)), Number(key.slice(comma + 1))]
}

/** The rectangles a storey's walls stand on: a post on each wall's cell, joined to its east and north neighbours. */
export const wallFootprints = (walls: ReadonlySet<string>): { x0: number; y0: number; x1: number; y1: number; cell: string }[] => {
    const out: { x0: number; y0: number; x1: number; y1: number; cell: string }[] = []
    const { left, right, depth } = WALL_BODY
    walls.forEach(cell => {
        const [x, y] = parse(cell)
        const n = walls.has(`${x},${y - 1}`), s = walls.has(`${x},${y + 1}`)
        const e = walls.has(`${x + 1},${y}`), w = walls.has(`${x - 1},${y}`)
        const bottom = y * T + T, top = bottom - depth
        // The open end of an east-west run is drawn out to the cell's edge.
        const run = !n && !s
        const x0 = run && e && !w ? 0 : left
        const x1 = run && w && !e ? T : right
        out.push({ x0: x * T + x0, y0: top, x1: x * T + x1, y1: bottom, cell })
        if (e) out.push({ x0: x * T + right, y0: top, x1: (x + 1) * T + left, y1: bottom, cell })
        if (n) out.push({ x0: x * T + left, y0: y * T, x1: x * T + right, y1: top, cell })
    })
    return out
}

/**
 * How far a pitched roof cell rises over its eave at a ground y: 45-degree slopes up to a ridge
 * halfway along its north-south run, which is where the art draws the ridge (the top row, or one row
 * down when the whole ridge drops there -- see pitchedRoofPiece).
 */
export const roofRise = (roofs: ReadonlySet<string>, x: number, y: number, groundY: number): number => {
    const isRoof = (cx: number, cy: number) => roofs.has(`${cx},${cy}`)
    let top = y, bottom = y
    while (isRoof(x, top - 1)) top--
    while (isRoof(x, bottom + 1)) bottom++
    const depth = bottom - top + 1
    if (depth === 1) return T / 4
    const drops = pitchedRoofPiece(isRoof, x, top).startsWith("back.")
    const ridge = (T / 2) * depth - (drops ? T : 0)
    const middle = ((top + bottom + 1) * T) / 2
    return Math.max(0, ridge * (1 - Math.abs(groundY - middle) / ((depth * T) / 2)))
}

const casters = (stack: readonly ShadowStorey[], receiver: number, options: BuildingShadowOptions): Caster[] => {
    const out: Caster[] = []
    const byFloor = new Map(stack.map(storey => [storey.floor, storey]))
    stack.forEach(storey => {
        if (storey.floor < receiver) return
        const base = (storey.floor - receiver) * options.rise
        const above = byFloor.get(storey.floor + 1)
        wallFootprints(storey.walls).forEach(({ cell, ...rect }) => {
            // A wall carrying another storey's wall runs up into it; a top wall ends at its own art.
            out.push({ ...rect, low: base, high: base + (above?.walls.has(cell) ? options.rise : WALL_TOP) })
        })
        if (storey.floor > receiver) storey.slab.forEach(cell => {
            const [x, y] = parse(cell)
            out.push({ x0: x * T, y0: y * T, x1: x * T + T, y1: y * T + T, low: base, high: base })
        })
        storey.roofs.forEach(cell => {
            const [x, y] = parse(cell)
            const pitched = storey.pitched.has(cell)
            // A pitched cell in quarter strips, so a gable end casts its point.
            const strip = pitched ? T / 4 : T
            for (let s = 0; s < T; s += strip) {
                const h = base + options.roofLift + (pitched ? roofRise(storey.roofs, x, y, y * T + s + strip / 2) : 0)
                out.push({ x0: x * T, y0: y * T + s, x1: x * T + T, y1: y * T + s + strip, low: h, high: h })
            }
        })
    })
    return out
}

/** The convex hull of points given as flat pairs (monotone chain), as flat pairs. */
const hull = (points: number[]): ShadowPolygon => {
    const pts: [number, number][] = []
    for (let i = 0; i < points.length; i += 2) pts.push([points[i], points[i + 1]])
    pts.sort((a, b) => a[0] - b[0] || a[1] - b[1])
    const cross = (o: number[], a: number[], b: number[]) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0])
    const lower: [number, number][] = [], upper: [number, number][] = []
    for (const p of pts) {
        while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop()
        lower.push(p)
    }
    for (let i = pts.length - 1; i >= 0; i--) {
        const p = pts[i]
        while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop()
        upper.push(p)
    }
    return [...lower.slice(0, -1), ...upper.slice(0, -1)].flat()
}

/** A caster's footprint swept along the sun from its lowest point to its highest. */
const sweep = ({ x0, y0, x1, y1, low, high }: Caster, sun: { x: number; y: number }): ShadowPolygon => {
    const at = (h: number) => {
        const dx = sun.x * h, dy = sun.y * h
        return [x0 + dx, y0 + dy, x1 + dx, y0 + dy, x1 + dx, y1 + dy, x0 + dx, y1 + dy]
    }
    return low === high ? at(low) : hull([...at(low), ...at(high)])
}

/** A convex polygon cut to an axis-aligned rectangle (Sutherland-Hodgman); empty when nothing is left. */
export const clipToRect = (polygon: ShadowPolygon, x0: number, y0: number, x1: number, y1: number): ShadowPolygon => {
    let points = polygon
    const edges: [(x: number, y: number) => boolean, (ax: number, ay: number, bx: number, by: number) => [number, number]][] = [
        [(x) => x >= x0, (ax, ay, bx, by) => [x0, ay + ((x0 - ax) / (bx - ax)) * (by - ay)]],
        [(x) => x <= x1, (ax, ay, bx, by) => [x1, ay + ((x1 - ax) / (bx - ax)) * (by - ay)]],
        [(_, y) => y >= y0, (ax, ay, bx, by) => [ax + ((y0 - ay) / (by - ay)) * (bx - ax), y0]],
        [(_, y) => y <= y1, (ax, ay, bx, by) => [ax + ((y1 - ay) / (by - ay)) * (bx - ax), y1]],
    ]
    for (const [inside, cross] of edges) {
        const out: number[] = []
        const n = points.length / 2
        for (let i = 0; i < n; i++) {
            const ax = points[i * 2], ay = points[i * 2 + 1]
            const j = (i + 1) % n
            const bx = points[j * 2], by = points[j * 2 + 1]
            const aIn = inside(ax, ay), bIn = inside(bx, by)
            if (aIn) out.push(ax, ay)
            if (aIn !== bIn) out.push(...cross(ax, ay, bx, by))
        }
        points = out
        if (points.length < 6) return []
    }
    return points
}

/**
 * The shade on storey `receiver` of `stack` (every storey of the building that is known, any order),
 * as convex polygons in that storey's pixels, already cut to where it can land. Draw them as one solid
 * union and apply the shadow's alpha once, so overlaps never darken twice.
 */
export const castBuildingShadows = (stack: readonly ShadowStorey[], receiver: number, options: BuildingShadowOptions): ShadowPolygon[] => {
    const self = stack.find(storey => storey.floor === receiver)
    if (!self) return []
    const upper = stack.find(storey => storey.floor === receiver + 1)
    // Indoors exempt: under this storey's roof or the floor of the storey above stays lit.
    const exempt = options.indoors ? undefined : new Set([...self.roofs, ...(upper?.slab ?? [])])
    const hasFloor = (x: number, y: number) => self.surface.has(`${x},${y}`)
    // The parts of a cell that receive: the whole cell on the ground, the drawn floor upstairs.
    const receiving = (x: number, y: number): FloorRect[] => {
        const key = `${x},${y}`
        if (exempt?.has(key)) return []
        if (self.ground) return [{ x0: x * T, y0: y * T, x1: x * T + T, y1: y * T + T }]
        return self.surface.has(key) ? drawnFloorRects(x, y, hasFloor) : []
    }
    const whole = (rects: FloorRect[]) => rects.length === 1 && rects[0].x1 - rects[0].x0 === T && rects[0].y1 - rects[0].y0 === T
    const unclipped = self.ground && !exempt
    const polygons: ShadowPolygon[] = []
    casters(stack, receiver, options).forEach(caster => {
        const polygon = sweep(caster, options.sun)
        if (unclipped) { polygons.push(polygon); return }
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
        for (let i = 0; i < polygon.length; i += 2) {
            minX = Math.min(minX, polygon[i]); maxX = Math.max(maxX, polygon[i])
            minY = Math.min(minY, polygon[i + 1]); maxY = Math.max(maxY, polygon[i + 1])
        }
        const cx0 = Math.floor(minX / T), cx1 = Math.ceil(maxX / T) - 1
        const cy0 = Math.floor(minY / T), cy1 = Math.ceil(maxY / T) - 1
        const rows: FloorRect[][] = []
        let all = true
        for (let cy = cy0; cy <= cy1; cy++) {
            const row: FloorRect[] = []
            for (let cx = cx0; cx <= cx1; cx++) {
                const rects = receiving(cx, cy)
                all = all && whole(rects)
                row.push(...rects)
            }
            rows.push(row)
        }
        if (all) { polygons.push(polygon); return }
        // Cut it to the receiving rectangles, joined along each band of each row.
        rows.forEach(row => {
            const bands = new Map<string, FloorRect[]>()
            row.forEach(rect => {
                const key = `${rect.y0}:${rect.y1}`
                const band = bands.get(key)
                const last = band?.[band.length - 1]
                if (last && last.x1 === rect.x0) last.x1 = rect.x1
                else if (band) band.push({ ...rect })
                else bands.set(key, [{ ...rect }])
            })
            bands.forEach(band => band.forEach(({ x0, y0, x1, y1 }) => {
                const piece = clipToRect(polygon, x0, y0, x1, y1)
                if (piece.length) polygons.push(piece)
            }))
        })
    })
    return polygons
}

/** Whether a point lies in a convex polygon (either winding). */
export const insidePolygon = (polygon: ShadowPolygon, x: number, y: number): boolean => {
    let sign = 0
    const n = polygon.length / 2
    for (let i = 0; i < n; i++) {
        const ax = polygon[i * 2], ay = polygon[i * 2 + 1]
        const j = (i + 1) % n
        const bx = polygon[j * 2], by = polygon[j * 2 + 1]
        const cross = (bx - ax) * (y - ay) - (by - ay) * (x - ax)
        if (cross === 0) continue
        if (sign === 0) sign = Math.sign(cross)
        else if (Math.sign(cross) !== sign) return false
    }
    return true
}

/** Whether a point lies in any of the polygons. */
export const shadedAt = (polygons: readonly ShadowPolygon[], x: number, y: number): boolean =>
    polygons.some(polygon => insidePolygon(polygon, x, y))
